import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import { inquiryService } from '@/services/InquiryService';
import { InquiryState, RootState } from '@/types/store';
import {
  CargoTypeEnum,
  InquiryFields,
  InquiryInput,
  InquiryScopedInput,
  QuoteSectionEnum,
  TransportationTypeEnum,
  deleteQuoteVariables,
  fetchQuotes_inquiries,
  upsertQuoteVariables,
  simulateQuote_simulateQuote
} from '@/types/graphql/schemaTypes';
import i18n from '@/plugins/i18n';

const defaultQuoteSection = {
  billItems: [],
  total: 0,
  totalCost: 0,
  totalMargin: 0,
  selected: true
};

const defaultSimulatedQuote: simulateQuote_simulateQuote = {
  details: {
    extra: { ...defaultQuoteSection },
    arrivalDrayage: { ...defaultQuoteSection },
    arrivalTrucking: { ...defaultQuoteSection },
    departureDrayage: { ...defaultQuoteSection },
    departureTrucking: { ...defaultQuoteSection },
    transport: { ...defaultQuoteSection }
  },
  margin: 0.15,
  totalCost: 0,
  totalMargin: 0,
  id: Date.now().toString(), // simulate uuid
  inquiry: {
    arrivalAddress: null,
    arrivalPlace: null,
    cargoDescription: {
      arrivalOptions: null,
      containers: null,
      departureOptions: null,
      description: null,
      overrideMeasures: false,
      parcels: null,
      regulated: false,
      totalVolume: 0,
      totalWeight: 0
    },
    cargoType: CargoTypeEnum.LCL,
    departureAddress: null,
    departurePlace: null,
    transportationType: TransportationTypeEnum.sea
  }
};

// initial state
const state: InquiryState = {
  action: '',
  editDetails: null,
  generatedQuote: null,
  inquiry: null,
  simulatedQuotes: [],
  isSimulation: false
};

// getters
// Here goes getter functions
const getters: GetterTree<InquiryState, RootState> = {
  action(inquiryState: InquiryState): string {
    return inquiryState.action;
  },
  inquiry(inquiryState: InquiryState): InquiryFields | null {
    if (inquiryState.inquiry) {
      return { ...inquiryState.inquiry };
    }
    return null;
  },
  simulatedQuote(inquiryState: InquiryState) {
    return inquiryState.simulatedQuotes;
  }
};

// actions
// Here goes services calls
const actions: ActionTree<InquiryState, RootState> = {
  async acceptInquiry({ commit }, args: InquiryScopedInput) {
    inquiryService.acceptInquiry(args).then((response) => {
      if (response.data!.acceptInquiry) {
        commit('setAction', 'accept');
      }
    });
  },
  async clearEdit({ commit }) {
    commit('clearEditDetails');
  },
  async deleteInquiry({ commit }, args: InquiryInput) {
    inquiryService.deleteInquiry(args).then((response) => {
      if ((response.data as any)!.deleteInquiry) {
        commit('setAction', 'delete');
      }
    });
  },
  async unarchiveInquiry({ commit }, args: string) {
    inquiryService.unarchiveInquiry(args).then((response) => {
      if (response.data) {
        commit('setAction', 'delete');
      }
    });
  },
  async fetchInquiry({ commit }, args: InquiryScopedInput) {
    inquiryService.fetchInquiry(args).then((response) => {
      commit('setInquiry', response.data.inquiries[0]);
    }).catch(() => {
      commit('Alert/addAlert', {
        type: 'error',
        message: i18n.t('error.fetch'),
        time: 5000
      }, { root: true });
    });
  },
  async refreshInquiry({ dispatch }) {
    if (state.inquiry && state.inquiry.id) {
      dispatch('fetchInquiry', { inquiryId: state.inquiry.id });
    }
  },
  async setAction({ commit }, action: string) {
    commit('setAction', action);
  },
  async setDetails({ commit }, args: InquiryFields) {
    commit('setEditDetails', args);
  },
  async updateRemoteInquiry({ commit }, args: InquiryInput) {
    try {
      const response = await inquiryService.updateInquiry(args);

      if (response.data!.updateInquiry) {
        commit('setInquiry', response.data!.updateInquiry);
        commit('Alert/addAlert', {
          type: 'success',
          message: i18n.t('success.updated'),
          time: 3000
        }, { root: true });
      }
    } catch (error) {
      commit('Alert/addAlert', {
        type: 'error',
        message: error.message,
        time: 5000
      }, { root: true });
      throw error;
    }
  },
  async updateInquiry({ commit, dispatch }, args: InquiryInput) {
    await dispatch('updateRemoteInquiry', args);
    commit('setAction', 'update');
  },
  // Quote related
  async fetchQuotes({ commit }, args: InquiryScopedInput) {
    inquiryService.fetchQuotes(args).then((response) => {
      commit('setQuotes', response.data.inquiries[0]);
    }).catch(() => {
      commit('Alert/addAlert', {
        type: 'error',
        message: i18n.t('error.fetch'),
        time: 5000
      }, { root: true });
    });
  },
  async deleteQuote({ commit, dispatch, state }, quoteId: deleteQuoteVariables) {
    if (state.isSimulation) {
      commit('deleteSimulatedQuote', quoteId);
    } else {
      try {
        const response = await inquiryService.deleteQuote(quoteId);

        if (response) {
          dispatch('fetchQuotes', { inquiryId: state.generatedQuote && state.generatedQuote.id });
          commit('Alert/addAlert', {
            type: 'success',
            message: i18n.t('success.deleted'),
            time: 5000
          }, { root: true });
        }
      } catch (error) {
        commit('Alert/addAlert', {
          type: 'error',
          message: i18n.t('error.delete'),
          time: 5000
        }, { root: true });
      }
    }
  },
  async selectQuote({ commit }, selectQuoteArgs: {
    quoteId: string;
    selectedSections: QuoteSectionEnum[];
  }) {
    try {
      await inquiryService.selectQuote(selectQuoteArgs.quoteId, selectQuoteArgs.selectedSections);
    } catch (error) {
      commit('Alert/addAlert', {
        type: 'error',
        message: error.message,
        time: 5000
      }, { root: true });
    }
  },
  async upsertQuote({ commit, dispatch, state }, upsertQuoteArgs: upsertQuoteVariables) {
    try {
      if (state.isSimulation) {
        const oldQuoteId = upsertQuoteArgs.quoteId; // to replace with new one from serve resp
        const response = await inquiryService.simulateQuote(upsertQuoteArgs);

        if (response.data) {
          commit('updateSimulatedQuote', { oldQuoteId, quote: (response.data as any).simulateQuote });
        }
      } else {
        await inquiryService.upsertQuote(upsertQuoteArgs);
        dispatch('fetchQuotes', { inquiryId: state.generatedQuote && state.generatedQuote.id });
      }
    } catch (error) {
      commit('Alert/addAlert', {
        type: 'error',
        message: error.message,
        time: 5000
      }, { root: true });
    }
  },
  // Simulated Quote related
  async addSimulatedQuote({ commit }) {
    commit('addSimulatedQuotes');
  },
  async copySimulatedQuote({ commit }, index: string) {
    commit('copySimulatedQuote', index);
  },
  async resetSimulatedQuote({ commit }) {
    commit('setSimulation', true);
    commit('resetSimulatedQuotes');
    commit('addSimulatedQuotes');
  },
  async setSimulation({ commit }, simulation: boolean) {
    commit('setSimulation', simulation);
  },
  async simulatedQuote({ commit }, upsertQuoteArgs: upsertQuoteVariables) {
    try {
      const oldQuoteId = upsertQuoteArgs.quoteId; // to replace with new one from serve resp
      const response = await inquiryService.simulateQuote(upsertQuoteArgs);

      if (response.data) {
        commit('updateSimulatedQuote', { oldQuoteId, quote: (response.data as any & { simulateQuote: any }).simulateQuote });
      }
    } catch (error) {
      commit('Alert/addAlert', {
        type: 'error',
        message: error.message,
        time: 5000
      }, { root: true });
    }
  }
};

// mutations
// Here goes state changer functions
const mutations: MutationTree<InquiryState> = {
  // TODO: State type and inquiry type
  clearEditDetails(inquiryState) {
    inquiryState.editDetails = null;
  },
  setAction(inquiryState, action: string) {
    inquiryState.action = action;
  },
  setEditDetails(inquiryState, inquiry: InquiryFields) {
    inquiryState.editDetails = inquiry;
  },
  setInquiry(inquiryState, inquiry: InquiryFields) {
    inquiryState.inquiry = inquiry;
  },
  // quote related
  setQuotes(inquiryState, quotes: fetchQuotes_inquiries) {
    inquiryState.generatedQuote = quotes;
  },
  // simulated quote related
  addSimulatedQuotes(inquiryState) {
    inquiryState.simulatedQuotes.push({ ...defaultSimulatedQuote });
  },
  copySimulatedQuote(inquiryState, index: number) {
    inquiryState.simulatedQuotes.push({ ...inquiryState.simulatedQuotes[index] });
  },
  deleteSimulatedQuote(inquiryState, quoteId: string) {
    if (!inquiryState.simulatedQuotes) {
      return;
    }
    const index = inquiryState.simulatedQuotes.findIndex((quote: any) => {
      return quote.id === quoteId;
    });

    inquiryState.simulatedQuotes.splice(index, 1);
  },
  resetSimulatedQuotes(inquiryState) {
    inquiryState.simulatedQuotes = [];
  },
  setSimulation(inquiryState, simulation: boolean) {
    inquiryState.isSimulation = simulation;
  },
  updateSimulatedQuote(inquiryState, args: { oldQuoteId: string; quote: any }) {
    if (!inquiryState.simulatedQuotes) {
      return;
    }
    const index = inquiryState.simulatedQuotes.findIndex((quote: any) => {
      return quote.id === args.oldQuoteId;
    });
    inquiryState.simulatedQuotes[index] = { ...args.quote };
  }
};

export const inquiry: Module<InquiryState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
